// Shared derivation of what a gallery block displays. Used by GalleryBlock.vue
// (render) and computePreloadImages (publish-time preload hints) so the two can
// never drift apart: any change to visibility or ordering rules lands in both.

// True if a work carries any of the given tag ids, in any tag group.
function workHasAnyTag(work, tagIds) {
  const groups = work?.tags || {}
  return Object.values(groups).some(ids => Array.isArray(ids) && ids.some(id => tagIds.includes(id)))
}

// Synced gallery ("all works", optionally narrowed by tag) mode: visible works
// sorted by gallery position. Archived (hidden) works never render in this mode.
// tagIds empty/undefined means all works; otherwise a work needs any one of them
// (OR, not AND - picking two tags grows the gallery, it doesn't narrow it further).
export function syncedGalleryImages(allWorks, tagIds) {
  const hasTagFilter = Array.isArray(tagIds) && tagIds.length > 0
  return (allWorks || [])
    .filter(work => work.isWorkShown !== false)
    .filter(work => !hasTagFilter || workHasAnyTag(work, tagIds))
    .map(work => ({
      ...work,
      thumbnailUrl: work.thumbnailUrl || work.imageThumb,
      order: work.position ?? 0
    }))
    .sort((workA, workB) => (workA.order ?? 0) - (workB.order ?? 0))
}

// Custom gallery (admin-picked selection): resolve the picks against the works
// list, in the admin's stored order. Entries are {workId, order} refs (normalized -
// resolved via the store for fresh data; workId may also be a slug) or legacy
// embedded display data, which is also the fallback for archived works absent from
// the visitor store. Unlike synced galleries, custom ones CAN show archived works:
// with includeArchived, hidden works render too.
export function customGalleryImages(images, allWorks, { includeArchived = false } = {}) {
  const worksByIdOrSlug = new Map()
  for (const work of allWorks || []) {
    worksByIdOrSlug.set(work.id, work)
    worksByIdOrSlug.set(work.slug, work)
  }

  return (images || [])
    .map((img, index) => {
      // Store lookup first - gives fresh data for works that are still around.
      if (img.workId) {
        const work = worksByIdOrSlug.get(img.workId)
        if (work) {
          return {
            ...work,
            thumbnailUrl: work.thumbnailUrl || work.imageThumb,
            caption: img.caption || work.caption || '',
            order: img.order ?? index
          }
        }
      }
      // Fallback: embedded display data.
      return { ...img, order: img.order ?? index }
    })
    .filter(img => img.thumbnailUrl || img.imageThumb) // drop unresolved references
    .filter(img => includeArchived || img.isWorkShown !== false)
    .sort((imgA, imgB) => (imgA.order ?? 0) - (imgB.order ?? 0))
}
